import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { createLocation } from 'history'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { postNotification } from 'src/app/Notifications/redux/notifications.actions'
import {
  selectLoadingFromState,
  selectNotificationsListModelsFromState,
  selectNotificationsUnreadCountFromState,
} from 'src/app/Notifications/redux/notifications.selectors'
import HomeActivityEmptyState from 'src/components/HomeActivityBox/HomeActivityEmptyState'
import HomeNotificationsTableItem from 'src/components/HomeActivityBox/HomeNotificationsSection/HomeNotificationsTableItem'
import LoadingBoxBottom from 'src/components/LoadingBoxBottom/LoadingBoxBottom'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  NotificationListModel,
  NotificationStatus,
} from 'src/models/notifications.models'
import { connectedPush } from 'src/navigation/Lnk'
import { activityUrl } from 'src/navigation/urls'
import { LocationState } from 'src/redux/createRootReducer'
import { selectIsCspAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

const HomeNotificationsSection: React.FC = () => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  const notifications = Object.values(
    useSelector(selectNotificationsListModelsFromState),
  )
  const notificationsUnreadCount = useSelector(
    selectNotificationsUnreadCountFromState,
  )
  const loading = useSelector(selectLoadingFromState)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const dispatch = useFlyoutDispatch()

  const handleSeeMore = (): void => {
    dispatch(
      connectedPush({
        pathname: activityUrl(),
        search: '?tab=notifications',
        hash: '',
        state: {},
      }),
    )
  }

  const handleNotificationClick = (
    id: string,
    status: string,
    action: string,
  ): void => {
    if (!isCspAuthenticated && status === NotificationStatus.unread) {
      analytics?.track('mark notification read', {
        screen: `/notifications`,
      })
      dispatch(postNotification(id))
    }
    if (action) {
      dispatch(connectedPush(createLocation<LocationState>(action)))
    }
  }

  const getRecentUnread = (
    notifications: NotificationListModel[],
    count = 5,
  ): NotificationListModel[] => {
    return [...notifications]
      .filter((n) => n.status === NotificationStatus.unread)
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
      .slice(0, count)
  }

  const latestFive = getRecentUnread(notifications)

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;

        ${getBreakpointDown(breakpointSizes.XSM)(css`
          padding: 40px 10px;
        `)};
      `}
    >
      <div
        css={css`
          display: flex;
          padding: 20px 20px 12px 20px;
        `}
      >
        <div
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {translate('Unread Notifications')}
        </div>
        <span
          onClick={(): void => handleSeeMore()}
          onKeyPress={(): null => {
            return null
          }}
          tabIndex={0}
          role={'button'}
          css={css`
            cursor: pointer;
            margin-left: auto;
            text-decoration: underline;
            ${getFontCss(
              14,
              themeConstants.colors.cta,
              styleConstants.fontFamilies.medium,
            )};
            opacity: ${notificationsUnreadCount > 0 ? 1 : 0.5};
          `}
        >
          {translate('See more')}
        </span>
      </div>
      <div>
        {latestFive.length > 0 &&
          latestFive.map((notification, index) => (
            <HomeNotificationsTableItem
              key={index}
              notification={notification}
              handleNotificationClick={handleNotificationClick}
            />
          ))}
        {!loading && latestFive.length === 0 && (
          <div
            css={css`
              display: flex;
              padding: 8px 20px 0;
            `}
          >
            <HomeActivityEmptyState
              sublabel={
                "Inbox zero achieved! You've read all your notifications."
              }
            />
          </div>
        )}
        {loading && <LoadingBoxBottom />}
      </div>
    </div>
  )
}

export default HomeNotificationsSection
